import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "./useAuth";
import { toast } from "@/hooks/use-toast";

// Notifications are served by agent-portal-api (/api/notifications), which
// queries the Supabase user_alerts table via Prisma. Real-time updates arrive
// over Socket.IO in NotificationSocketProvider and are written into the same
// React Query cache keys used below.

export interface UserAlert {
  id: number;
  user_id: string;
  alert_type_id: number;
  title: string;
  message: string;
  is_read: boolean;
  read_at: string | null;
  related_entity_type: string | null;
  related_entity_id: string | null;
  metadata: unknown;
  created_date: string;
  alert_type?: {
    alert_code: string;
    title: string;
    description: string | null;
  } | null;
}

export const notificationsQueryKey = (userId: string | undefined, includeRead: boolean) =>
  ["notifications", userId ?? "anon", includeRead] as const;

export const unreadCountQueryKey = (userId: string | undefined) =>
  ["notifications", "unread-count", userId ?? "anon"] as const;

export function useUserAlerts(includeRead = false) {
  const { user } = useAuth();
  return useQuery<UserAlert[]>({
    queryKey: notificationsQueryKey(user?.id, includeRead),
    queryFn: () =>
      api.get<UserAlert[]>(`/api/notifications?include_read=${includeRead}&limit=50`),
    enabled: !!user,
    staleTime: 30_000,
  });
}

export function useUnreadAlertCount(): number {
  const { user } = useAuth();
  const { data } = useQuery<{ count: number }>({
    queryKey: unreadCountQueryKey(user?.id),
    queryFn: () => api.get<{ count: number }>("/api/notifications/unread-count"),
    enabled: !!user,
    staleTime: 30_000,
  });
  return data?.count ?? 0;
}

export function useMarkAlertRead() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: (alertId: number) =>
      api.patch<{ id: number; is_read: true }>(`/api/notifications/${alertId}/read`),
    onMutate: async (alertId) => {
      // Optimistic — flip is_read in any cached notifications query for this user.
      await queryClient.cancelQueries({ queryKey: ["notifications", user?.id ?? "anon"] });
      queryClient.setQueriesData<UserAlert[]>(
        { queryKey: ["notifications", user?.id ?? "anon"] },
        (prev) =>
          prev?.map((a) => (a.id === alertId ? { ...a, is_read: true } : a)) ?? prev,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: unreadCountQueryKey(user?.id) });
    },
    onError: (err: Error) => {
      toast.error(err.message || "Couldn't mark notification as read");
      queryClient.invalidateQueries({ queryKey: ["notifications", user?.id ?? "anon"] });
    },
  });
}

export function useMarkAllAlertsRead() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: () => api.post<{ updated: number }>("/api/notifications/mark-all-read"),
    onSuccess: () => {
      queryClient.setQueriesData<UserAlert[]>(
        { queryKey: ["notifications", user?.id ?? "anon"] },
        (prev) => prev?.map((a) => ({ ...a, is_read: true })) ?? prev,
      );
      queryClient.invalidateQueries({ queryKey: unreadCountQueryKey(user?.id) });
    },
    onError: (err: Error) => {
      toast.error(err.message || "Couldn't mark all as read");
    },
  });
}
